import React, { useEffect, useState } from 'react';
import { Loader2, Lock, FileText, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { QuizRunner } from './QuizRunner';
import { mmHeaders } from './WeekView';

export const ProjectCard = ({ period, saved, onSaved }) => {
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (text.trim().length < 150) return toast.error('Write at least 150 characters.');
    setBusy(true);
    try {
      const r = await api.post(`/academy/money-moves/period/${period.n}/project`, { text, link: link || null }, { headers: mmHeaders() });
      onSaved(period.n, { score: r.data.best, feedback: r.data.feedback });
      toast.success(`Project graded: ${r.data.score}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-2 border-zinc-800 p-5" data-testid={`mm-project-${period.n}`}>
      <div className="flex items-center gap-2 text-[10px] font-black tracking-[0.3em] uppercase text-emerald-400"><FileText className="w-4 h-4" /> Period {period.n} Project</div>
      <h4 className="text-lg font-black text-white mt-1">{period.project.title}</h4>
      <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{period.project.brief}</p>
      {saved?.score !== undefined && saved?.score !== null && (
        <div className="mt-4 border-2 border-emerald-500/40 bg-emerald-500/5 p-3" data-testid={`mm-project-result-${period.n}`}>
          <div className="text-sm font-black text-white">Score: <span className="text-emerald-400">{saved.score}%</span></div>
          <p className="text-xs text-zinc-300 mt-1">{saved.feedback}</p>
        </div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder="Write your project here: your plan, your numbers, and your TANSTAAFL reasoning…" className="mt-4 w-full bg-black border-2 border-zinc-800 focus:border-emerald-500 outline-none p-3 text-sm text-white" data-testid={`mm-project-text-${period.n}`} />
      <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Optional link (Google Doc, Slides, video)" className="mt-2 w-full bg-black border-2 border-zinc-800 focus:border-emerald-500 outline-none px-3 py-2 text-xs text-white" data-testid={`mm-project-link-${period.n}`} />
      <button onClick={submit} disabled={busy} className="mt-3 flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest disabled:opacity-50" data-testid={`mm-project-submit-${period.n}`}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {saved?.score !== undefined ? 'Resubmit (best counts)' : 'Submit Project'}
      </button>
    </div>
  );
};

export const PeriodTest = ({ period, onDone }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let t; let alive = true;
    const load = () => api.get(`/academy/money-moves/period/${period.n}/test`, { headers: mmHeaders() }).then((r) => {
      if (!alive) return;
      setData(r.data);
      if (r.data.status === 'generating') t = setTimeout(load, 6000);
    }).catch(() => alive && setData({ status: 'error' }));
    load();
    return () => { alive = false; clearTimeout(t); };
  }, [open, period.n]);

  const submit = async (answers) => {
    if (!window.confirm('Submit your cumulative test? You only get one attempt.')) return;
    try {
      const r = await api.post(`/academy/money-moves/period/${period.n}/test/submit`, { answers }, { headers: mmHeaders() });
      setData({ status: 'submitted', result: r.data.result, questions: data.questions }); onDone(period.n, r.data.result.score);
      toast.success(`Test score: ${r.data.result.score}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
  };

  return (
    <div className="border-2 border-yellow-500/40 p-5" data-testid={`mm-test-${period.n}`}>
      <div className="flex items-center gap-2 text-[10px] font-black tracking-[0.3em] uppercase text-yellow-400"><GraduationCap className="w-4 h-4" /> Period {period.n} Cumulative Test</div>
      <p className="text-xs text-zinc-400 mt-2">20 questions on all 6 weeks. Opens at {period.test_unlock_pct}% of the period's lessons. One attempt.</p>
      {!open ? (
        <button onClick={() => setOpen(true)} className="mt-3 border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-widest transition-colors" data-testid={`mm-test-open-${period.n}`}>Open Test</button>
      ) : !data || data.status === 'generating' ? (
        <div className="py-6 flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin text-yellow-400" /> Preparing your test…</div>
      ) : data.status === 'locked' ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-zinc-300" data-testid={`mm-test-locked-${period.n}`}><Lock className="w-4 h-4 text-zinc-500" /> Finish {data.need}% of this period's lessons first (you're at {data.pct}%).</div>
      ) : data.status === 'submitted' ? (
        <div className="mt-3" data-testid={`mm-test-score-${period.n}`}>
          <div className="text-sm font-black text-white mb-4">Your score: <span className="text-yellow-400">{data.result.score}%</span></div>
          {data.questions?.length > 0 && <QuizRunner questions={data.questions} result={data.result} onSubmit={() => {}} testidPrefix={`mm-test-review-${period.n}`} />}
        </div>
      ) : data.status === 'ready' ? (
        <div className="mt-4"><QuizRunner questions={data.questions} onSubmit={submit} submitLabel="Submit Test" testidPrefix={`mm-test-q-${period.n}`} /></div>
      ) : <div className="text-sm text-amber-400 mt-3" data-testid={`mm-test-error-${period.n}`}>{data.detail || 'Could not load the test.'}</div>}
    </div>
  );
};
